import i18next from "i18next";
import { useCallback, useState } from "react";

type UserLocation = {
  latitude: number;
  longitude: number;
};

type UseUserLocationReturn = {
  userLocation: UserLocation | null;
  isGettingLocation: boolean;
  locationError: string | null;
  getUserLocation: () => void;
  clearUserLocation: () => void;
};

/**
 * Hook for accessing user's geolocation with retry logic.
 */
export function useUserLocation(): UseUserLocationReturn {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  /** Converts geolocation error codes to German error messages. */
  const getLocationErrorMessage = useCallback((error: GeolocationPositionError): string => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return i18next.t("common:GeolocationAccessHasBeenDenied");
      case error.POSITION_UNAVAILABLE:
        return i18next.t("masterdata:LocationUnavailable");
      case error.TIMEOUT:
        return i18next.t("masterdata:LocationTimeout");
      default:
        return i18next.t("common:ErrorGettingPosition");
    }
  }, []);

  /** Requests user location with high accuracy, retries with low accuracy on failure. */
  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(i18next.t("common:GeolocationNotSupported"));
      return;
    }

    setIsGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setIsGettingLocation(false);
        setLocationError(null);
      },
      (error) => {
        if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setUserLocation({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
              setIsGettingLocation(false);
            },
            (fallbackError) => {
              setLocationError(getLocationErrorMessage(fallbackError));
              setIsGettingLocation(false);
            },
            {
              enableHighAccuracy: false,
              timeout: 15000,
              maximumAge: 60000,
            },
          );
        } else {
          setLocationError(getLocationErrorMessage(error));
          setIsGettingLocation(false);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 60000,
      },
    );
  }, [getLocationErrorMessage]);

  /** Clears user location and error state. */
  const clearUserLocation = useCallback(() => {
    setUserLocation(null);
    setLocationError(null);
  }, []);

  return {
    userLocation,
    isGettingLocation,
    locationError,
    getUserLocation,
    clearUserLocation,
  };
}
